import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
  HomeworkPagination,
  IQuestionStatus,
  IQuestionStatusObject,
  IStyles,
  Loader,
  NoContentCard,
  PrimaryButton,
  QuestionContainerWithSolution,
  cloneObject,
  getLastSignificantState,
  getOmrQuestionState,
  mapOmrResponseToAssessmentResponse,
  mapOmrResponseToStudentResponseFetch,
  getLocalStorage,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import {
  AssessmentEnum,
  AssignmentConfigEnum,
  AttemptTypeEnum,
  MarkingSchemeEnum,
  MultiSubjectSection,
  QAttemptResultEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { TeacherStudentResponseFetch } from '@protos/learning_management/lms.hw.teacher.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../Auth/reducer/auth.slice';
import { HwPerformanceStatCard } from './component/HwPerformanceStatCard';
import ViewStudentReportButton from '../components/ViewStudentReportButton';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { ProctoringDataTable } from './component/ProctoringDataTable';
import { getSubjectNameFromEnum } from '../../Home/home.utils';
import { getQuestionNumberOffsetForSection } from '../../Assessment/utils/assessment.utils';
import { TeacherStudentOmrResponseFetchResponse } from '@protos/learning_management/lms.assessment.teacher.apis_pb';

const styles: IStyles = {
  root: {
    boxSizing: 'border-box',
    p: {
      xs: `0`,
      md: `${pxTovW(20)} ${pxTovW(20)}`,
      lg: `${pxTovW(60)} ${pxTovW(240)}`,
    },
    display: 'flex',
    justifyContent: 'space-between',
  },

  secondaryHeadingBox: {
    mb: { xs: pxToRem(20), md: pxTovW(20) },
    pl: { xs: pxToRem(20), md: '0' },
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(8), md: pxTovW(12) },
    flexWrap: 'wrap',
  },

  userImage: {
    width: { xs: pxToRem(60), md: pxTovW(87) },
    height: { xs: pxToRem(60), md: pxTovW(87) },
  },

  questionBox: {
    margin: 'auto',
    // width: {xs: 'max-content', md: '100%'},
    mt: { xs: pxToRem(20), md: pxTovW(20) },
  },
};

export const HwPerformance = () => {
  const subjectTabsScrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({
    isPointerDown: false,
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
    pointerId: -1,
  });
  const { student_id } = useParams();
  const parsedRouteStudentId = Number(student_id);
  const studentIdBigInt = Number.isFinite(parsedRouteStudentId)
    ? BigInt(parsedRouteStudentId)
    : undefined;
  const selected_assessment_id = useDeserifiedSelector(
    (state) => state.manageHomework.selected_assessment_id
  );
  const [questionStatusArray, setQuestionStatusArray] = useState<
    IQuestionStatusObject[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [hwContent, setHwContent] = useState<AssessmentContent | undefined>();
  const [studentResponse, setStudentResponse] = useState<
    TeacherStudentResponseFetch | undefined
  >();
  const [studentOmrResponse, setStudentOmrResponse] = useState<
    TeacherStudentOmrResponseFetchResponse | undefined
  >();
  const [selectedSection, setSelectedSection] = useState<
    MultiSubjectSection | undefined
  >();
  // const [currentResponse, setCurrentResponse] = useState<StudentTaskResponse>();
  //^ HwPagination
  const [index, setIndex] = useState(0);
  const {
    LmsHomewokTeacherAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const dispatch = useAppDispatch();
  const content = hwContent?.assessmentContent || [];
  const [searchParams, setSearchParams] = useSearchParams();
  const attemptNumber = searchParams.get('attemptNumber');
  const isBestAttempt = searchParams.get('isBestAttempt');
  const omrMode = searchParams.get('omrMode');
  const omrStudentId = searchParams.get('omrStudentId');
  const taskQuestionInfoSubjectWise =
    hwContent?.questionsSequenceInfo?.multiSubjectSections
      .find((section) => section.sectionNo === selectedSection?.sectionNo)
      ?.multiSubjectSubSections.map(
        (subSection) => subSection.subSectionMetaData
      )
      .flat() || [];

  // -------------------------------------------------------------

  const questionNumberOffset = useMemo(
    () =>
      getQuestionNumberOffsetForSection({
        multiSubjectSections:
          hwContent?.questionsSequenceInfo?.multiSubjectSections,
        currentSectionNo: selectedSection?.sectionNo,
      }),
    [
      hwContent?.questionsSequenceInfo?.multiSubjectSections,
      selectedSection?.sectionNo,
    ]
  );

  // -------------------------------------------------------------

  const selectedOmrSet =
    omrMode === 'true'
      ? hwContent?.questionsSequenceInfo?.omrSetInfo?.find(
          (set) => set.omrSetName === (studentOmrResponse?.setName || 'A')
        )
      : undefined;

  const omrSectionQuestions = useMemo(() => {
    if (!selectedOmrSet?.omrSectionInfo?.length) return [];

    const omrSections = selectedOmrSet.omrSectionInfo;
    // Multi-subject: match by subjectId; single-section: use first section
    const omrSection = selectedSection
      ? omrSections.find((s) => s.subjectId === selectedSection.subjectId) ??
        omrSections[0]
      : omrSections[0];

    return omrSection?.omrQuestionsSequenceInfo ?? [];
  }, [selectedOmrSet, selectedSection]);

  const taskQuestionInfo =
    hwContent?.questionsSequenceInfo?.taskQuestionInfo || [];
  const sortedQuestionIds =
    omrMode === 'true'
      ? (omrSectionQuestions
          .slice()
          .sort((a, b) => a.sequence - b.sequence)
          .map((val) => val.questionId)
          .filter((val) => Boolean(val)) as string[])
      : selectedSection
      ? (taskQuestionInfoSubjectWise
          .sort((a, b) => a.sequence - b.sequence)
          .map((val) => val.questionId)
          .filter((val) => val !== undefined) as string[])
      : (taskQuestionInfo
          .sort((a, b) => a.sequence - b.sequence)
          .map((val) => val.questionId)
          .filter((val) => val !== undefined) as string[]);
  // Sort the content based on the sortedQuestionIds
  const sortedContent = sortedQuestionIds
    .map((questionId) => content.find((item) => item.questionId === questionId))

    .filter((item) => item !== undefined);

  const statCardStudentResponse =
    omrMode === 'true'
      ? mapOmrResponseToStudentResponseFetch({
          omrResponse: studentOmrResponse,
          assessmentContent: hwContent,
          options: {
            studentName: omrStudentId?.split(' - ')[0]?.trim(),
            studentIdFromRoute: student_id,
          },
        })
      : studentResponse;

  const selectedQuestion = sortedContent[index];
  const currentResponse =
    omrMode === 'true'
      ? mapOmrResponseToAssessmentResponse(
          studentOmrResponse?.studentResponses.find(
            (e) => e.questionId === selectedQuestion?.questionId
          )
        )
      : studentResponse?.responses.find(
          (e) => e.questionId === selectedQuestion?.questionId
        );
  const totalQuestionCount = sortedContent.length;
  const selectedPage = index + 1;
  const teacher_id = getLocalStorage('userId');

  // -------------------------------------------------------------

  const remarksAndMarksSaveHandler = async (reqObj: {
    questionId: string;
    sessionId: bigint;
    remarks: string[];
    obtainedMarks: number;
  }) => {
    try {
      setLoading(true);
      const response =
        await LmsHomewokTeacherAPIServiceV1ClientWithStatusCodeHandler.updateTeacherReview(
          {
            ...reqObj,
            teacherId: teacher_id,
            homeworkId: selected_assessment_id,
            studentId: studentIdBigInt,
          }
        );
      setLoading(false);
      dispatch(
        setToastInfo({
          label: 'marks/remarks updated successfully',
          variant: 'success',
          open: true,
        })
      );
      fetchData();
    } catch (err) {
      setLoading(false);
      dispatch(
        setToastInfo({
          label: 'marks/remarks not updated',
          variant: 'error',
          open: true,
        })
      );
      console.log(err);
    }
  };

  // -------------------------------------------------------------

  const totalMarks = content.reduce((acc, val) => {
    const marks =
      val.question?.model.value?.commonQuestionContent?.marks.reduce(
        (a, b) => a + b,
        0
      ) || 0;
    return acc + marks;
  }, 0);

  // -------------------------------------------------------------

  useEffect(() => {
    if (!sortedContent.length) {
      setQuestionStatusArray([]);
      return;
    }

    if (omrMode === 'true') {
      if (!studentOmrResponse) return;

      const res = sortedContent.map((e, i) => {
        if (!e) {
          return { index: i, statusInfo: IQuestionStatus.NotAttempted };
        }

        const omrResponse = studentOmrResponse.studentResponses.find(
          (response) => response.questionId === e.questionId
        );

        if (!omrResponse?.isSubmitted) {
          return { index: i, statusInfo: IQuestionStatus.NotAttempted };
        }

        if (omrResponse.isCorrect) {
          return { index: i, statusInfo: IQuestionStatus.Correct };
        }

        return { index: i, statusInfo: IQuestionStatus.Incorrect };
      });
      setQuestionStatusArray(res);
      return;
    }

    if (!studentResponse) return;

    const res = sortedContent.map((e, i) => {
      const questionResponse = studentResponse.responses.find(
        //@ts-ignore
        (res) => res.questionId === e.questionId
      );

      //  Is there currently a valid answer at the end of the timeline?
      const historyState = getLastSignificantState(
        questionResponse?.responses || []
      );

      const isActuallyAttempted = historyState.status === 'lastAnswered';

      const result = isActuallyAttempted
        ? questionResponse?.isCorrect
        : QAttemptResultEnum.RESPONSE_UNDEFINED;

      switch (result) {
        case QAttemptResultEnum.RESPONSE_CORRECT:
          return { index: i, statusInfo: IQuestionStatus.Correct };
        case QAttemptResultEnum.RESPONSE_INCORRECT:
        case QAttemptResultEnum.RESPONSE_PARTIALLY_CORRECT:
          return { index: i, statusInfo: IQuestionStatus.Incorrect };
        default:
          return { index: i, statusInfo: IQuestionStatus.NotAttempted };
      }
    });
    setQuestionStatusArray(res);
  }, [
    hwContent,
    studentResponse,
    studentOmrResponse,
    selectedSection,
    omrMode,
  ]);

  // -------------------------------------------------------------

  const fetchOmrData = async () => {
    try {
      setLoading(true);
      setError(false);
      const [studentOmrResponse, hwContent] = await Promise.all([
        LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.getStudentAssessmentOmrResponse(
          {
            // studentId: student_id,
            studentId: omrStudentId || '',
            assessmentId: selected_assessment_id,
          }
        ),
        LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: teacher_id,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: selected_assessment_id,
          }
        ),
      ]);

      // console.log(studentResponse.data);
      if (studentOmrResponse && hwContent.data) {
        // setStudentResponse(studentResponse.data);
        setStudentOmrResponse(studentOmrResponse);
        const assessment = hwContent.data;
        const taskQuestionInfo =
          assessment.questionsSequenceInfo?.taskQuestionInfo || [];
        const marksMap: Record<string, number> = {};
        taskQuestionInfo.forEach((item) => {
          if (item.questionId) {
            marksMap[item.questionId] = Number(item.maxMarks ?? 1);
          }
        });
        const updatedHw = cloneObject(assessment);
        updatedHw.assessmentContent = updatedHw.assessmentContent.map((q) => {
          if (!q.questionId) return q;
          const customMark = marksMap[q.questionId];
          if (
            q.question?.model?.value?.commonQuestionContent &&
            customMark !== undefined
          ) {
            q.question.model.value.commonQuestionContent.marks = [customMark];
          }
          return q;
        });
        setHwContent(updatedHw);
        setLoading(false);
      } else {
        setError(true);
      }
    } catch (err) {
      setLoading(false);
      setError(true);
      console.error(err);
    }
  };

  // -------------------------------------------------------------

  useEffect(() => {
    if (
      omrMode === 'true' &&
      omrStudentId &&
      selected_assessment_id &&
      teacher_id
    ) {
      fetchOmrData();
    } else if (
      studentIdBigInt !== undefined &&
      selected_assessment_id &&
      teacher_id
    ) {
      fetchData();
    }
  }, []);

  // -------------------------------------------------------------

  const handlePageChange = (page: number | null) => {
    // console.log('page:', page);
    if (page) setIndex(page - 1);
  };

  // -------------------------------------------------------------

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(false);
      const [studentResponse, hwContent] = await Promise.all([
        LmsHomewokTeacherAPIServiceV1ClientWithStatusCodeHandler.getStudentHWResponse(
          {
            teacherId: teacher_id,
            studentId: studentIdBigInt,
            homeworkId: selected_assessment_id,
            attemptNumber: attemptNumber ? Number(attemptNumber) : undefined,
            attemptType: isBestAttempt
              ? AttemptTypeEnum.ATTEMPT_TYPE_BEST
              : undefined,
          }
        ),
        LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: teacher_id,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: selected_assessment_id,
          }
        ),
      ]);
      // console.log(studentResponse.data);
      if (studentResponse.data && hwContent.data) {
        setStudentResponse(studentResponse.data);
        const assessment = hwContent.data;
        const taskQuestionInfo =
          assessment.questionsSequenceInfo?.taskQuestionInfo || [];
        const marksMap: Record<string, number> = {};
        taskQuestionInfo.forEach((item) => {
          if (item.questionId) {
            marksMap[item.questionId] = Number(item.maxMarks ?? 1);
          }
        });
        const updatedHw = cloneObject(assessment);
        updatedHw.assessmentContent = updatedHw.assessmentContent.map((q) => {
          if (!q.questionId) return q;
          const customMark = marksMap[q.questionId];
          if (
            q.question?.model?.value?.commonQuestionContent &&
            customMark !== undefined
          ) {
            q.question.model.value.commonQuestionContent.marks = [customMark];
          }
          return q;
        });
        setHwContent(updatedHw);
        setLoading(false);
      } else {
        setError(true);
      }
    } catch (err) {
      setLoading(false);
      setError(true);
      console.error(err);
    }
  };

  // -------------------------------------------------------------

  // Reset page index when section changes
  useEffect(() => {
    setIndex(0);
  }, [selectedSection]);

  // -------------------------------------------------------------

  // Set initial section
  useEffect(() => {
    if (hwContent?.multiSubjectSections) {
      setSelectedSection(hwContent.multiSubjectSections[0]);
    }
  }, [hwContent]);

  // -------------------------------------------------------------

  const handleSubjectTabsPointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = subjectTabsScrollRef.current;
    if (!container) return;

    dragStateRef.current = {
      isPointerDown: true,
      isDragging: false,
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
      pointerId: event.pointerId,
    };
  };

  // -------------------------------------------------------------

  const handleSubjectTabsPointerMove = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = subjectTabsScrollRef.current;
    if (!container) return;
    if (!dragStateRef.current.isPointerDown) return;
    if (dragStateRef.current.pointerId !== event.pointerId) return;

    // If mouse button is no longer pressed, reset drag state.
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      dragStateRef.current.isPointerDown = false;
      dragStateRef.current.isDragging = false;
      dragStateRef.current.pointerId = -1;
      return;
    }

    const deltaX = event.clientX - dragStateRef.current.startX;

    if (!dragStateRef.current.isDragging) {
      if (Math.abs(deltaX) < 5) return; // ignore tiny jitter — treat as click
      dragStateRef.current.isDragging = true;
      container.setPointerCapture(event.pointerId);
    }

    container.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
  };

  const handleSubjectTabsPointerUp = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = subjectTabsScrollRef.current;
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
    if (container?.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
  };

  const handleSubjectTabsPointerLeave = () => {
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
  };

  const handleSubjectTabsPointerCancel = () => {
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
  };

  return loading === true ? (
    <Loader />
  ) : error === true ? (
    <NoContentCard variant="error" text="Error Occured" icon="cards" />
  ) : (
    <Grid container spacing={2} sx={styles.root}>
      <Grid
        item
        xs={12}
        md={4.8}
        sx={{ mb: { xs: pxToRem(20), md: pxTovW(20) } }}
      >
        <HwPerformanceStatCard
          studentResponse={statCardStudentResponse}
          totalMarks={
            omrMode === 'true' ? studentOmrResponse?.totalMarks : totalMarks
          }
          hwContent={hwContent}
        />

        {selected_assessment_id &&
        (studentIdBigInt !== undefined ||
          (omrMode === 'true' && omrStudentId)) ? (
          <ViewStudentReportButton
            modeOfConduction={hwContent?.assessment?.modeOfConduction}
            studentId={studentIdBigInt}
            omrStudentId={omrStudentId}
            homeworkId={selected_assessment_id}
            isJeeReport={
              hwContent?.assessment?.markingSchemeType ===
              MarkingSchemeEnum.MARKING_SCHEME_JEE
            }
            isMultiSubjectAssessment={
              hwContent?.assessment?.taskType ===
              TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM
            }
            subject={
              hwContent?.assessment?.subjects?.length
                ? hwContent.assessment.subjects.join('_')
                : hwContent?.assessment?.subject
            }
            className={hwContent?.assessment?.class}
            section={hwContent?.assessment?.section}
          />
        ) : null}
        {hwContent?.assessment?.assignmentInfo?.find(
          (info) =>
            info.configName ===
            AssignmentConfigEnum.ASSIGNMENT_CONFIG_PROCTORING
        )?.isEnabled && studentResponse?.metrics?.proctoringMetrics ? (
          <ProctoringDataTable
            proctoringMetrics={studentResponse?.metrics?.proctoringMetrics}
          />
        ) : null}
      </Grid>

      {sortedContent.length !== 0 && (
        <Grid item xs={12} md={7}>
          <Box sx={styles.secondaryHeadingBox}>
            <Typography variant="h2" fontWeight="bold">
              Answer & Explanations
            </Typography>
            {omrMode === 'true' ? (
              <>
                <Typography variant="h2" fontWeight="bold" color="#000">
                  -
                </Typography>
                <Typography variant="h2" fontWeight="bold" color="#007CDC">
                  Set {studentOmrResponse?.setName || 'A'}
                </Typography>
              </>
            ) : null}
          </Box>
          <Box
            ref={subjectTabsScrollRef}
            onPointerDown={handleSubjectTabsPointerDown}
            onPointerMove={handleSubjectTabsPointerMove}
            onPointerUp={handleSubjectTabsPointerUp}
            onPointerLeave={handleSubjectTabsPointerLeave}
            onPointerCancel={handleSubjectTabsPointerCancel}
            sx={{
              display: 'block',
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
              cursor: 'grab',
              touchAction: 'pan-x',
              userSelect: 'none',

              '&:active': {
                cursor: 'grabbing',
              },
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {hwContent?.assessment?.assessmentType ===
              AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT &&
            hwContent?.assessment?.taskType ===
              TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM ? (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  width: 'max-content',
                  minWidth: '100%',
                  gap: { xs: pxToRem(8), md: pxTovW(20) },
                  px: { xs: pxToRem(6), md: pxTovW(12) },
                  py: { xs: pxToRem(10), md: pxTovW(10) },
                  backgroundColor: '#F3F9FE',
                  borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                  boxSizing: 'border-box',
                  '&::before, &::after': {
                    content: '""',
                    width: { xs: pxToRem(6), md: pxTovW(10) },
                    flex: '0 0 auto',
                  },
                }}
              >
                {hwContent.multiSubjectSections.map((section, index) => {
                  const subject = `${getSubjectNameFromEnum(section.subject)}`;
                  const isSelected = selectedSection === section;

                  return (
                    <Box
                      key={subject}
                      onClick={() => setSelectedSection(section)}
                      sx={{
                        flex: '0 0 auto',
                        scrollSnapAlign: 'start',
                        height: { xs: pxToRem(32), md: pxTovW(50) },
                        minWidth: { xs: pxToRem(100), md: pxTovW(150) },
                        px: { xs: pxToRem(12), md: pxTovW(14) },
                        borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.25s ease',
                        backgroundColor: isSelected ? '#007CDC' : 'transparent',
                        color: isSelected ? '#fff' : '#000',
                        '&:hover': {
                          backgroundColor: isSelected ? '#007CDC' : '#E3F2FD',
                        },
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={isSelected ? 'bold' : 500}
                        color="inherit"
                        noWrap
                      >
                        {subject}{' '}
                        {section.sectionTitle
                          ? `- ${section.sectionTitle}`
                          : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              overflowX: 'scroll',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              px: { xs: pxToRem(16), md: 0 },
            }}
          >
            <HomeworkPagination
              currentPage={selectedPage}
              totalPages={totalQuestionCount}
              showPreviousNextButtons={totalQuestionCount >= 10}
              questionStatusArray={questionStatusArray}
              onPageChange={handlePageChange}
              maxButtonsToDisplay={10}
              questionNumberOffset={questionNumberOffset}
            />
            {/* <HwPaginationButtons
            totalPages={content.length}
            currentPage={selectedPage}
            displayButtons={content.length}
            onPageChange={handlePageChange}
            colored
          /> */}
          </Box>

          {/* Question Renderer */}
          {/* <Box
          sx={{
            width: 'auto',
            // height: {xs: pxToRem(420), md: pxTovW(400)},
            // bgcolor: 'neutral.turquoise',
            border: `1px solid #CED2FC`,
            borderRadius: {
              xs: pxToRem(15),
              md: pxTovW(15),
            },
            backgroundColor: 'common.white',
            marginTop: {
              xs: pxToRem(15),
              md: pxTovW(15),
            },
          }}
        >
          <Box
            sx={{
              padding: {
                xs: `${pxToRem(20)} ${pxToRem(10)}`,
                md: `${pxTovW(20)} ${pxTovW(50)}`,
              },
            }}
          >
            {selectedQuestion && (
              <QuestionContainer
                questionNumber={index + 1 + questionNumberOffset}
                questionContent={selectedQuestion.question}
                userAttemptedAnswer={answers[index]}
              />
            )}
          </Box>
          <Box
            sx={{
              borderTop: '1px solid #CED2FC',
              padding: {
                xs: `${pxToRem(15)} ${pxToRem(13)}`,
                md: `${pxTovW(24)} ${pxTovW(30)}`,
              },
              backgroundColor: '#F3F9FE',
            }}
          >
            <TypographyHtml variant="bodyText">
              {selectedQuestion &&
                'Correct Answer: ' + getSolutionText(selectedQuestion)}
            </TypographyHtml>
          </Box>
        </Box> */}
          <Box
            sx={{
              marginTop: {
                xs: pxToRem(15),
                md: pxTovW(15),
              },
              pb: { xs: pxToRem(16), md: 0 },
              px: { xs: pxToRem(16), md: 0 },
            }}
          >
            {selectedQuestion && (
              <QuestionContainerWithSolution
                key={selectedQuestion.questionId}
                questionNumber={index + 1 + questionNumberOffset}
                question={selectedQuestion}
                disableInput={true}
                showAnswer
                isSubmitted
                userAttemptedAnswer={
                  omrMode === 'true'
                    ? getOmrQuestionState(
                        studentOmrResponse?.studentResponses,
                        selectedQuestion.questionId
                      ).answer
                    : getLastSignificantState(
                        studentResponse?.responses.find(
                          (res) =>
                            res.questionId === selectedQuestion.questionId
                        )?.responses || []
                      ).answer // Always returns [] if cleared or null
                }
                currentResponse={currentResponse}
                // taskEvaluationStatus ={studentResponse?.taskEvaluationStatus}
                saveClickHandlerInsideModal={remarksAndMarksSaveHandler}
                imageResizeEnabled={true}
              />
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
